import { TableRowCallForActionButton } from 'components/buttons/CallForActionButton';
import PermissionButton from 'components/buttons/PermissionButton';
import { draftPageOf } from 'components/create-dialog/recordTypes';
import { MoreVert, PencilIcon, PersonAdd, UserPlusIcon } from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import KebabMenu, { MenuItem } from 'components/navigation/KebabMenu';
import {
    META_LINK_CLASS,
    MetaItem,
    OUTLINED_ACTION_CLASS,
    PRIMARY_ACTION_CLASS,
    RecordMoreMenu,
} from 'components/record-header/RecordHeader';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { isToday } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import type { DraftDetail, DraftKey } from 'types/drafts';
import { cn } from 'utils/cn';
import { formatTimeOfDay } from 'utils/date';
import { t } from 'utils/getTranslations';
import { useEmployeeNameFormatter } from 'utils/nameOrder';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { type DraftActions, useDraftActions } from './useDraftActions';

/**
 * A draft record page's header actions, as far as the reader's right on the draft goes: Edit, Share and ⋯ › Delete
 * draft in the new design; ⋮ › Delete draft, Edit and Share in classic. The page mounts `actions.shareDialog`.
 */
export function DraftRecordActions({ actions }: { actions: DraftActions }) {
    const isNew = useIsNewDesign();
    const menuItems: MenuItem[] = actions.canDelete
        ? [{ handler: actions.askDelete, title: t('delete-draft'), variant: 'destructive' }]
        : [];
    const edit = (
        <PermissionButton
            hasAuthority={actions.canEdit}
            variant="outlined"
            size="small"
            className={
                isNew ? cn(OUTLINED_ACTION_CLASS, 'px-3.5 font-semibold has-data-[icon=inline-start]:pl-3') : undefined
            }
            startIcon={isNew ? <PencilIcon className="size-4" /> : undefined}
            onClick={actions.edit}
        >
            <FormattedMessage id="edit" />
        </PermissionButton>
    );
    const share = (
        <PermissionButton
            hasAuthority={actions.canShare}
            variant="contained"
            color="secondary"
            size="small"
            className={isNew ? cn(PRIMARY_ACTION_CLASS, 'has-data-[icon=inline-start]:pl-3') : undefined}
            startIcon={isNew ? <UserPlusIcon className="size-4" /> : <PersonAdd />}
            onClick={actions.share}
        >
            {t('draft.share.submit')}
        </PermissionButton>
    );

    return isNew ? (
        <>
            {edit}
            {share}
            <RecordMoreMenu menuItems={menuItems} />
        </>
    ) : (
        <>
            {menuItems.length > 0 ? (
                <KebabMenu menuItems={menuItems} icon={<MoreVert fontSize="medium" />} className="h-10" />
            ) : null}
            {edit}
            {share}
        </>
    );
}

/**
 * A draft row's menu in a list: Open for whoever sees the draft, Share and Delete draft for its owner. `className`
 * sizes its button, where it sits in a cell rather than in the actions column.
 */
export function DraftRowMenu({ draft, className }: { draft: DraftKey; className?: string }) {
    const navigateWithReturn = useNavigateWithReturnState();
    const actions = useDraftActions({ draft, listed: true });

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger
                    render={
                        <TableRowCallForActionButton shape="rounded" aria-label={t('actions')} className={className}>
                            <MoreVert />
                        </TableRowCallForActionButton>
                    }
                />
                <DropdownMenuContent align="end" className="w-auto">
                    <DropdownMenuItem onClick={() => navigateWithReturn(draftPageOf(draft.type, draft.id))}>
                        {t('create-dialog.saved.open')}
                    </DropdownMenuItem>
                    {actions.canShare ? (
                        <DropdownMenuItem onClick={actions.share}>{t('draft.share.submit')}</DropdownMenuItem>
                    ) : null}
                    {actions.canDelete ? (
                        <DropdownMenuItem variant="destructive" onClick={actions.askDelete}>
                            {t('delete-draft')}
                        </DropdownMenuItem>
                    ) : null}
                </DropdownMenuContent>
            </DropdownMenu>
            {actions.shareDialog}
        </>
    );
}

/** When a draft was last saved: "Saved today at 14:20", or on an earlier day. */
export function DraftSavedAt({ at }: { at: string }) {
    const savedAt = new Date(at);
    return isToday(savedAt) ? (
        <FormattedMessage id="create-dialog.saved.today" values={{ time: formatTimeOfDay(savedAt) }} />
    ) : (
        <FormattedMessage id="create-dialog.saved.on" values={{ date: <FormattedDate key="date" value={savedAt} /> }} />
    );
}

/**
 * Who created a draft, a link to them, and when it was last saved, in its header: the new design's meta items,
 * classic's "Created by:" line among the header's own.
 */
export function DraftMeta({ draft }: { draft: DraftDetail }) {
    const isNew = useIsNewDesign();
    const formatName = useEmployeeNameFormatter();
    const { owner } = draft;
    const ownerLink = owner ? (
        <RecordLink
            className={isNew ? META_LINK_CLASS : 'text-link no-underline hover:underline'}
            to={`/employee/${owner.id}`}
            label={formatName(owner)}
        />
    ) : null;
    if (!isNew) {
        return ownerLink ? (
            <p className="text-caption leading-[1.6667] font-medium">
                <FormattedMessage id="created-by" />: {ownerLink}
            </p>
        ) : null;
    }
    return (
        <>
            {ownerLink ? <MetaItem label={<FormattedMessage id="created-by" />}>{ownerLink}</MetaItem> : null}
            <span>
                <DraftSavedAt at={draft.lastSavedAt} />
            </span>
        </>
    );
}
